/**
 * Reading a tracking number as it is typed, and sending it somewhere useful.
 *
 * Two jobs. The first is the one the page exists for: decide which carrier can
 * read this number and open them. The second is what makes the page feel like
 * it is paying attention — recognising the number while it is still being
 * typed, and redrawing the route to match.
 *
 * What it never does is claim to know where the parcel is. No storefront can
 * read a carrier's status, and a progress bar guessing at it would be exactly
 * the dishonesty this page was built to undo. It shows the route; the carrier
 * shows the position.
 *
 * Carrier matching mirrors src/tracking-carriers.mjs in the control tower,
 * minus the branch that needs a carrier name the storefront does not have.
 */
(() => {
  const form = document.querySelector('[data-track-form]');
  if (!form) return;

  const field = form.querySelector('#zc-track-number');
  const result = form.querySelector('[data-track-result]');
  const chip = form.querySelector('[data-track-chip]');
  const route = document.querySelector('[data-track-route]');
  const empty = document.querySelector('[data-track-empty]');
  const panel = document.querySelector('[data-track-parcel]');
  const stops = route?.querySelector('.zc-track__stops');
  const orderField = form.querySelector('[name="orderName"]');
  const emailField = form.querySelector('[name="email"]');

  /** Where our own answer comes from. No third party is asked. */
  const API = 'https://control.zoocare.se/api/public/tracking';

  const escape = (value) => String(value ?? '').replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));

  /**
   * Show the journey only once there is a parcel to show it for.
   *
   * The route used to be on the page from the moment it loaded, which
   * described a shipment to someone who had not said they had one.
   */
  const revealRoute = (show) => {
    if (route) route.hidden = !show;
    if (empty) empty.hidden = show;
    if (!show && panel) panel.hidden = true;
  };


  /** Swedish short dates: "14 sep" for this year, with the year when not. */
  const thisYear = new Date().getFullYear();
  const shortDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
      ...(date.getFullYear() === thisYear ? {} : { year: 'numeric' }),
    });
  };

  /**
   * Draw one parcel's six steps, with the date each one happened.
   *
   * A step can be done without a date: a delivered parcel was necessarily
   * collected even when nobody logged the collection. Showing the tick without
   * a date is honest; borrowing a date from the next step would not be.
   */
  const drawSteps = (steps) => {
    if (!stops) return;
    stops.innerHTML = steps.map((step, index) => (
      `<li style="--i: ${index}" data-done="${step.done}" data-now="${step.now}">
        <span class="zc-track__dot" aria-hidden="true"></span>
        <span class="zc-track__stop-name">${escape(step.label)}</span>
        ${step.at ? `<time class="zc-track__stop-date" datetime="${escape(step.at)}">${escape(shortDate(step.at))}</time>` : ''}
      </li>`
    )).join('');
  };

  /** Which parcel of an order is on screen. */
  let shipments = [];
  let active = 0;

  const showShipment = (index) => {
    active = index;
    const parcel = shipments[index];
    if (!parcel || !panel) return;

    const tabs = shipments.length > 1
      ? `<div class="zc-track__tabs" role="tablist">${shipments.map((other, i) => (
          `<button type="button" role="tab" data-shipment="${i}" aria-selected="${i === index}">${form.dataset.parcelWord || 'Paket'} ${other.index}</button>`
        )).join('')}</div>`
      : '';

    const items = parcel.items.map((item) => (
      `<li>
        ${item.image
          // Decorative: the title beside it already names the product, so a
          // screen reader would only hear it twice.
          ? `<img src="${escape(item.image)}" alt="" loading="lazy" width="48" height="48">`
          : '<span class="zc-track__thumb-none" aria-hidden="true"></span>'}
        <span class="zc-track__item-text"><b>${item.quantity}&times;</b> ${escape(item.title)}${
          item.variant ? ` <span>${escape(item.variant)}</span>` : ''}</span>
      </li>`
    )).join('');

    panel.innerHTML = `
      ${tabs}
      <div class="zc-track__parcel-head">
        <h2>${escape(orderName)}${shipments.length > 1 ? ` &middot; ${form.dataset.parcelWord || 'Paket'} ${parcel.index}` : ''}</h2>
        <span class="zc-track__parcel-now">${escape(parcel.stepLabel)}</span>
      </div>
      <ul class="zc-track__parcel-items">${items}</ul>
      ${parcel.trackingNumber || destination
        ? `<p class="zc-track__parcel-meta">${
            parcel.trackingNumber ? `${escape(parcel.carrier || '')} <code>${escape(parcel.trackingNumber)}</code>` : ''}${
            parcel.trackingNumber && destination ? '<br>' : ''}${
            destination ? `${escape(form.dataset.toWord || 'Levereras till')} ${escape(destination)}` : ''}</p>`
        : ''}`;
    panel.hidden = false;
    drawSteps(parcel.steps);
    revealRoute(true);
  };

  let orderName = '';
  // The town, never the street. Enough for a customer who has moved, or who
  // sent a present, to see it is going to the right place.
  let destination = '';
  const showParcel = (answer) => {
    orderName = answer.orderName || '';
    shipments = answer.shipments || [];
    destination = answer.address
      ? [answer.address.zip, answer.address.city].filter(Boolean).join(' ')
      : '';
    if (!shipments.length) return;
    showShipment(0);
    bringIntoView();
  };

  /**
   * Move the answer into view once it exists.
   *
   * On a phone the lookup card fills the screen, so a parcel rendered below it
   * appears somewhere the customer never looks — they press the button, the
   * page seems to do nothing, and they press it again.
   *
   * Only on a fresh answer, never when switching between parcels: yanking the
   * page while somebody is comparing two tabs is its own annoyance.
   */
  const bringIntoView = () => {
    if (!panel) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // After layout: the parcel and the six steps above it both render in the
    // same tick, and measuring before either has settled aims at the wrong
    // place. scroll-margin-top on the panel keeps its heading clear of the
    // sticky header.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      panel.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
    }));
  };

  panel?.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-shipment]');
    if (tab) showShipment(Number(tab.dataset.shipment));
  });

  /** Ask our own server. */
  const lookup = async (body) => {
    const response = await fetch(API, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      // Our records do not have it. That is a different thing from not being
      // able to ask, and the two deserve different answers.
      const error = new Error(payload.error || 'Vi hittar ingen sändning med de uppgifterna.');
      error.notFound = true;
      throw error;
    }
    return payload;
  };


  /**
   * Formats belonging to one carrier and no other.
   *
   * Deliberately short. A guess that opens the wrong carrier is worse than the
   * aggregator, which reads any number and works it out.
   */
  const SHAPES = [
    { test: /^[A-Z]{2}\d{9}SE$/i, company: 'PostNord' },
    { test: /^1Z[0-9A-Z]{16}$/i, company: 'UPS' },
    { test: /^\d{20}$/, company: 'DHL' },
  ];

  /** Everything else: a number we cannot place to one carrier. */
  const AGGREGATOR = { company: null };

  /** What was typed, in the shape a carrier expects. */
  const clean = (value) => String(value || '').trim().replace(/\s+/g, '').toUpperCase();

  const resolve = (number) => SHAPES.find(({ test }) => test.test(number)) || AGGREGATOR;

  /**
   * Whether the number is complete enough to say anything about.
   *
   * Guessing from four characters would mean the chip flickering between
   * carriers while somebody types, which reads as broken rather than clever.
   */
  const readable = (number) => number.length >= 10;

  const say = (text, tone) => {
    result.textContent = text;
    result.className = `zc-track__result zc-track__result--${tone}`;
  };

  /** The live read, while typing. */
  const preview = () => {
    const number = clean(field.value);
    if (!readable(number)) {
      chip.hidden = true;
      revealRoute(false);
      return;
    }
    const found = resolve(number);
    chip.hidden = false;
    // A recognised carrier is named. An unrecognised number says what will be
    // done about it, which is the useful half of the sentence anyway.
    chip.textContent = found.company
      ? `${form.dataset.sniffing} ${found.company}`
      : form.dataset.aggregatorChip;
    revealRoute(true);
  };

  let timer;
  field.addEventListener('input', () => {
    // Debounced so the chip settles rather than flickering per keystroke.
    clearTimeout(timer);
    timer = setTimeout(preview, 140);
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const number = clean(field.value);

    if (!number) {
      if (await byOrder()) return;
      say(form.dataset.errorEmpty, 'warn');
      revealRoute(false);
      field.focus();
      return;
    }
    if (number.length < 8) {
      say(form.dataset.errorShort, 'warn');
      revealRoute(false);
      field.focus();
      return;
    }

    say(form.dataset.searching || 'Hämtar din sändning…', 'ok');
    try {
      showParcel(await lookup({ trackingNumber: number }));
      say('', 'ok');
    } catch (error) {
      // The number is not one of ours. If they also filled in the order
      // fields, that is a better answer than giving up on them.
      if (await byOrder()) return;

      revealRoute(false);
      // No carrier link, by decision: the customer stays with us. A number we
      // have no record of is answered here, and so is our own outage.
      say(error.notFound ? error.message : (form.dataset.offline || 'Vi når inte vår spårning just nu. Försök igen om en stund.'), 'warn');
    }
  });

  /** The second way in: order number and the email it was placed with. */
  const byOrder = async () => {
    const orderName = (orderField?.value || '').trim();
    const email = (emailField?.value || '').trim();
    if (!orderName || !email) return false;
    say(form.dataset.searching || 'Hämtar din sändning…', 'ok');
    try {
      showParcel(await lookup({ orderName, email }));
      say('', 'ok');
      return true;
    } catch (error) {
      say(error.message, 'warn');
      revealRoute(false);
      return false;
    }
  };

  // A number pasted into the address bar as ?number= opens straight from the
  // shipping mail, which is where most people arrive from.
  const fromUrl = new URLSearchParams(window.location.search).get('number');
  if (fromUrl) {
    field.value = clean(fromUrl);
    preview();
  }
})();
